import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Shield,
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Users,
  FileBarChart2,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ExternalLink,
  Copy,
  ShieldCheck,
  Sun,
  Moon,
  Gift,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";

const nav = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
  { key: "deposits", label: "Deposits", icon: ArrowDownToLine, path: "/admin/deposits" },
  { key: "withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, path: "/admin/withdrawals" },
  { key: "users", label: "Users", icon: Users, path: "/admin/users" },
  { key: "copy", label: "Copy Trading", icon: Copy, path: "/admin/copy-trading" },
  { key: "affiliates", label: "Affiliates", icon: Gift, path: "/admin/affiliates" },
  { key: "kyc", label: "KYC", icon: ShieldCheck, path: "/admin/kyc" },
  { key: "settings", label: "Settings", icon: Settings, path: "/admin/settings" },
  { key: "logs", label: "Audit log", icon: FileBarChart2, path: "/admin/logs", soon: true },
];

const AdminLayout = ({ children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const nav_ = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    nav_("/admin/login", { replace: true });
  };

  const NavLinks = () =>
    nav.map((it) => {
      const Icon = it.icon;
      const active = isActive(it.path);
      if (it.soon) {
        return (
          <div
            key={it.key}
            className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] text-white/35 cursor-not-allowed"
          >
            <Icon size={17} strokeWidth={1.8} />
            <span className="flex-1">{it.label}</span>
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.05] text-white/40">
              Soon
            </span>
          </div>
        );
      }
      return (
        <Link
          key={it.key}
          to={it.path}
          className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition-all ${
            active
              ? "bg-[#ff7a1a]/12 text-white border border-[#ff7a1a]/30"
              : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"
          }`}
        >
          <Icon size={17} strokeWidth={1.8} />
          <span>{it.label}</span>
        </Link>
      );
    });

  return (
    <div className={`min-h-screen bg-[#07080d] text-white flex ${theme === "light" ? "themed-light" : ""}`}>
      {/* Admin mode top strip */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1.5 bg-gradient-to-r from-[#ff7a1a] via-[#f97316] to-[#c2410c]"></div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] shrink-0 flex-col border-r border-white/5 bg-[#08090f] sticky top-0 h-screen pt-1.5">
        <div className="px-5 h-16 flex items-center border-b border-white/5">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#ff7a1a] to-[#c2410c] flex items-center justify-center">
              <Shield size={15} className="text-white" />
            </div>
            <span className="font-display font-bold text-[16px]">Admin Console</span>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-wider text-white/35 font-semibold">
            Menu
          </div>
          <NavLinks />
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#ff7a1a] to-[#c2410c] flex items-center justify-center text-[12px] font-semibold">
              {(user?.name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium text-white truncate">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-[#ffae6a] truncate uppercase tracking-wider">{user?.role?.replace("_", " ") || "admin"}</div>
            </div>
            <button onClick={handleLogout} className="text-white/40 hover:text-white" title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
          <Link to="/" className="mt-2 flex items-center gap-2 px-3 py-2 text-[11px] text-white/40 hover:text-white">
            <ExternalLink size={11} /> Open customer site
          </Link>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex pt-1.5">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}></div>
          <aside className="relative w-[280px] max-w-[82vw] bg-[#08090f] border-r border-white/5 flex flex-col">
            <div className="px-5 h-16 flex items-center justify-between border-b border-white/5">
              <Link to="/admin" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#ff7a1a] to-[#c2410c] flex items-center justify-center">
                  <Shield size={15} className="text-white" />
                </div>
                <span className="font-display font-bold text-[16px]">Admin</span>
              </Link>
              <button onClick={() => setDrawerOpen(false)} className="text-white/60"><X size={18} /></button>
            </div>
            <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto"><NavLinks /></nav>
            <button onClick={handleLogout} className="p-4 text-left text-[13px] text-white/70 border-t border-white/5 hover:text-white">
              <LogOut size={14} className="inline mr-2" /> Sign out
            </button>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col pt-1.5">
        <header className="h-16 border-b border-white/5 bg-[#07080d]/80 backdrop-blur-md sticky top-1.5 z-30 flex items-center justify-between px-4 lg:px-8 gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button onClick={() => setDrawerOpen(true)} className="lg:hidden w-9 h-9 rounded-lg border border-white/8 text-white/75 flex items-center justify-center">
              <Menu size={17} />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] lg:text-[11px] uppercase tracking-wider text-[#ffae6a] font-semibold">{subtitle || "Admin Console"}</div>
              <div className="font-display font-semibold text-[16px] lg:text-[18px] text-white truncate">{title || "Dashboard"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#ff7a1a]/15 border border-[#ff7a1a]/30 text-[10px] uppercase tracking-wider font-semibold text-[#ffae6a]">
              <Shield size={10} /> Admin Mode
            </span>
            <button
              onClick={toggle}
              data-testid="admin-theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
              <Bell size={15} />
            </button>
          </div>
        </header>

        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
